// §4.4 — public, unauthenticated demo split into two independent sections:
//   A) Regime Detection (HMM chart) — own ticker selector
//   B) Backtest Lab — own ticker selector + %OTM / exit-rule controls
// Data is precomputed static JSON (frozen Jan 2024 – Dec 2025); switching any
// control swaps between precomputed results — no live inference or DB hits.
import { useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TICKERS = ["SPY", "TSLA", "CAKE"];
const REGIME_COLORS = { Quiet: "#0ea5e9", Bull: "#10b981", Bear: "#ef4444", Vol: "#f59e0b" };
const OTM_OPTIONS = [5, 10, 15];
const DELTA_OPTIONS = [15, 25, 35];
const EXIT_OPTIONS = [
  ["hold", "Hold to expiry"],
  ["pt50", "Close at 50% PT"],
  ["pt70", "Close at 70% PT"],
  ["pt90", "Close at 90% PT"],
];

// Module-level cache shared by both sections — each ticker is fetched once.
const _cache = {};
const fetchDemo = ticker => {
  if (!_cache[ticker]) {
    _cache[ticker] = fetch(`${API}/api/demo/showcase/${ticker}`)
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .catch(e => { delete _cache[ticker]; throw e; });
  }
  return _cache[ticker];
};

const useDemoData = ticker => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    setError(null); setData(null);
    fetchDemo(ticker)
      .then(d => { if (alive) setData(d); })
      .catch(() => { if (alive) setError("Demo data temporarily unavailable."); });
    return () => { alive = false; };
  }, [ticker]);
  return { data, error };
};

const TickerToggle = ({ value, onChange, C, idPrefix }) => (
  <div className="flex gap-1.5" data-testid={`${idPrefix}-toggle`}>
    {TICKERS.map(t => (
      <button key={t} onClick={() => onChange(t)} data-testid={`${idPrefix}-${t.toLowerCase()}`}
        className="px-4 h-8 text-xs font-bold rounded-md transition-colors"
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          background: value === t ? C.accent : "transparent",
          color: value === t ? "#0a0a0a" : C.muted,
          border: `1px solid ${value === t ? C.accent : C.border}`,
        }}>
        {t}
      </button>
    ))}
  </div>
);

const WindowChip = ({ C }) => (
  <div className="text-[10px] mt-1 px-2 py-0.5 inline-block rounded" data-testid="demo-window-label"
    style={{ background: "rgba(79,195,247,0.1)", color: C.cyan, border: `1px solid rgba(79,195,247,0.3)`, fontFamily: "'JetBrains Mono', monospace" }}>
    Public Demo: 2-Year Historical Window (2024–2025)
  </div>
);

const SectionCard = ({ C, title, right, children, footer, testid }) => (
  <div className="rounded-lg overflow-hidden mb-6" style={{ background: C.card, border: `1px solid ${C.borderHover || C.border}` }} data-testid={testid}>
    <div className="px-4 pt-4 pb-3 flex flex-wrap items-center justify-between gap-3 border-b" style={{ borderColor: C.border }}>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>
          {title}
        </div>
        <WindowChip C={C} />
      </div>
      {right}
    </div>
    <div className="px-4 py-3">{children}</div>
    <div className="px-4 py-2 text-[9px] border-t" style={{ color: "#555", borderColor: C.border }}>{footer}</div>
  </div>
);

function RegimeChart({ data, C }) {
  const [hover, setHover] = useState(null);
  const candles = data.candles;
  const W = 900, H = 330, PAD = { top: 26, right: 52, bottom: 22, left: 8 };
  const plotW = W - PAD.left - PAD.right, plotH = H - PAD.top - PAD.bottom;
  const prices = candles.flatMap(c => [c.h, c.l]).concat(
    candles.map(c => c.s50).filter(Boolean), candles.map(c => c.s200).filter(Boolean));
  const minP = Math.min(...prices) * 0.99, maxP = Math.max(...prices) * 1.01;
  const range = maxP - minP || 1;
  const toY = p => PAD.top + plotH - ((p - minP) / range) * plotH;
  const toX = i => PAD.left + (i / candles.length) * plotW;
  const gap = plotW / candles.length;
  const cw = Math.max(0.8, gap * 0.65);

  const bands = [];
  candles.forEach((c, i) => {
    const last = bands[bands.length - 1];
    if (last && last.r === c.r) last.end = i;
    else bands.push({ r: c.r, start: i, end: i });
  });

  const smaPath = key => candles
    .map((c, i) => (c[key] == null ? null : `${i === 0 || candles[i - 1][key] == null ? "M" : "L"}${toX(i).toFixed(1)},${toY(c[key]).toFixed(1)}`))
    .filter(Boolean).join(" ");

  const yTicks = Array.from({ length: 5 }, (_, i) => minP + (range / 4) * i);
  const xInterval = Math.max(1, Math.floor(candles.length / 8));
  const hovered = hover != null ? candles[hover] : null;
  const hoverXPct = hover != null ? ((toX(hover) + gap / 2) / W) * 100 : 0;
  const tx = hoverXPct < 14 ? "0%" : hoverXPct > 86 ? "-100%" : "-50%";

  return (
    <div className="relative" onMouseLeave={() => setHover(null)}>
      {hovered && (
        <div className="absolute z-10 flex flex-wrap gap-x-2.5 gap-y-0.5 text-[10px] px-2 py-1 rounded whitespace-nowrap pointer-events-none"
          data-testid="demo-chart-tooltip"
          style={{
            left: `${hoverXPct}%`, top: "8%", transform: `translateX(${tx})`,
            background: "rgba(10,10,10,0.94)", border: `1px solid ${C.borderHover || C.border}`,
            color: C.muted, fontFamily: "'JetBrains Mono', monospace", backdropFilter: "blur(4px)",
          }}>
          <span style={{ color: C.text }}>{hovered.d}</span>
          <span>O:<span style={{ color: C.text }}>${hovered.o.toFixed(2)}</span></span>
          <span>H:<span style={{ color: C.text }}>${hovered.h.toFixed(2)}</span></span>
          <span>L:<span style={{ color: C.text }}>${hovered.l.toFixed(2)}</span></span>
          <span>C:<span style={{ color: hovered.c >= hovered.o ? "#10b981" : "#ef4444" }}>${hovered.c.toFixed(2)}</span></span>
          {hovered.r && (
            <span className="px-1 rounded-sm font-bold" style={{ background: REGIME_COLORS[hovered.r], color: "#000" }}>
              {hovered.r}
            </span>
          )}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: "auto", touchAction: "pan-y" }} data-testid="demo-chart">
        {bands.map((b, i) => (
          <rect key={`s${i}`} x={toX(b.start)} y={6} width={toX(b.end) + gap - toX(b.start)} height={9}
            fill={REGIME_COLORS[b.r] || "#555"} opacity={0.9}>
            <title>{b.r}: {candles[b.start].d} → {candles[b.end].d}</title>
          </rect>
        ))}
        {bands.map((b, i) => (
          <rect key={`b${i}`} x={toX(b.start)} y={PAD.top} width={toX(b.end) + gap - toX(b.start)} height={plotH}
            fill={REGIME_COLORS[b.r] || "#555"} opacity={0.07} />
        ))}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.left} x2={W - PAD.right} y1={toY(t)} y2={toY(t)} stroke={C.border} strokeWidth="0.5" />
            <text x={W - PAD.right + 6} y={toY(t) + 3} fontSize="9" fill={C.muted} fontFamily="'JetBrains Mono', monospace">
              {t >= 1000 ? t.toFixed(0) : t.toFixed(1)}
            </text>
          </g>
        ))}
        {candles.map((c, i) => {
          const up = c.c >= c.o;
          const x = toX(i) + gap / 2;
          return (
            <g key={c.d}>
              <line x1={x} x2={x} y1={toY(c.h)} y2={toY(c.l)} stroke={up ? "#10b981" : "#ef4444"} strokeWidth="0.7" />
              <rect x={x - cw / 2} y={toY(Math.max(c.o, c.c))} width={cw}
                height={Math.max(0.8, Math.abs(toY(c.o) - toY(c.c)))} fill={up ? "#10b981" : "#ef4444"} />
              {i % xInterval === 0 && (
                <text x={x} y={H - 6} fontSize="8.5" fill={C.muted} textAnchor="middle" fontFamily="'JetBrains Mono', monospace">
                  {c.d.slice(0, 7)}
                </text>
              )}
            </g>
          );
        })}
        <path d={smaPath("s50")} fill="none" stroke="#f59e0b" strokeWidth="1.2" opacity="0.9" />
        <path d={smaPath("s200")} fill="none" stroke="#4fc3f7" strokeWidth="1.2" opacity="0.9" />
        {hovered && (
          <line x1={toX(hover) + gap / 2} x2={toX(hover) + gap / 2} y1={PAD.top} y2={PAD.top + plotH}
            stroke={C.muted} strokeWidth="0.6" strokeDasharray="3 3" opacity="0.7" />
        )}
        {candles.map((c, i) => (
          <rect key={`hit-${c.d}`} x={toX(i)} y={PAD.top} width={Math.max(gap, 1)} height={plotH}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onPointerDown={() => setHover(i)} />
        ))}
      </svg>
    </div>
  );
}

function EquityCurve({ backtest, C }) {
  const pts = backtest.equity_curve;
  const W = 900, H = 150, PAD = { top: 10, right: 52, bottom: 20, left: 8 };
  const plotW = W - PAD.left - PAD.right, plotH = H - PAD.top - PAD.bottom;
  const vals = pts.map(p => p.pnl);
  const minV = Math.min(0, ...vals), maxV = Math.max(...vals);
  const range = maxV - minV || 1;
  const toY = v => PAD.top + plotH - ((v - minV) / range) * plotH;
  const toX = i => PAD.left + (i / Math.max(1, pts.length - 1)) * plotW;
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(p.pnl).toFixed(1)}`).join(" ");
  const area = `${line} L${toX(pts.length - 1).toFixed(1)},${toY(Math.max(0, minV)).toFixed(1)} L${toX(0).toFixed(1)},${toY(Math.max(0, minV)).toFixed(1)} Z`;
  const xInterval = Math.max(1, Math.floor(pts.length / 6));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: "auto" }} data-testid="demo-equity-curve">
      <line x1={PAD.left} x2={W - PAD.right} y1={toY(0)} y2={toY(0)} stroke={C.border} strokeWidth="0.7" strokeDasharray="3 3" />
      <text x={W - PAD.right + 6} y={toY(0) + 3} fontSize="9" fill={C.muted} fontFamily="'JetBrains Mono', monospace">$0</text>
      <path d={area} fill={C.accent} opacity="0.08" />
      <path d={line} fill="none" stroke={C.accent} strokeWidth="1.4" />
      {pts.map((p, i) => i % xInterval === 0 && (
        <text key={p.d} x={toX(i)} y={H - 5} fontSize="8.5" fill={C.muted} textAnchor="middle" fontFamily="'JetBrains Mono', monospace">
          {p.d.slice(0, 7)}
        </text>
      ))}
    </svg>
  );
}

// ── Section A: Regime Detection ──────────────────────────────
const RegimeSection = ({ C }) => {
  const [ticker, setTicker] = useState("SPY");
  const { data, error } = useDemoData(ticker);
  return (
    <SectionCard C={C} title="Regime Detection — HMM" testid="demo-regime-section"
      right={<TickerToggle value={ticker} onChange={setTicker} C={C} idPrefix="demo-ticker" />}
      footer="Historical sample data (Jan 2024 – Dec 2025), illustrative only — not a recommendation to buy, sell, or hold any security. Identical output for every visitor.">
      <p className="mb-3 text-[11px] leading-relaxed max-w-3xl" style={{ color: C.muted }} data-testid="demo-regime-explainer">
        This chart shows how the model classifies each trading day into one of four market regimes
        (Quiet, Bull, Bear, Vol) using a Hidden Markov Model trained on price behavior. Regimes are
        shown as colored bands so you can see how market conditions shifted over the last two years.
      </p>
      {error && <div className="h-40 flex items-center justify-center text-xs" style={{ color: C.muted }} data-testid="demo-error">{error}</div>}
      {!error && !data && <div className="h-40 flex items-center justify-center text-xs" style={{ color: C.muted }} data-testid="demo-loading">Loading demo data…</div>}
      {data && (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-1 text-[9px]" style={{ color: C.muted, fontFamily: "'JetBrains Mono', monospace" }}>
            <span style={{ color: C.text }}>REGIME:</span>
            {Object.entries(REGIME_COLORS).map(([l, c]) => (
              <span key={l} className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: c }} />{l}</span>
            ))}
            <span className="flex items-center gap-1 ml-2"><span className="w-3 h-0.5 inline-block" style={{ background: "#f59e0b" }} />SMA50</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ background: "#4fc3f7" }} />SMA200</span>
          </div>
          <RegimeChart data={data} C={C} />
        </>
      )}
    </SectionCard>
  );
};

// ── Section B: Backtest Lab ──────────────────────────────────
const BacktestSection = ({ C }) => {
  const [ticker, setTicker] = useState("SPY");
  const [strikeMode, setStrikeMode] = useState("otm");
  const [otm, setOtm] = useState(5);
  const [delta, setDelta] = useState(25);
  const [exitRule, setExitRule] = useState("hold");
  const { data, error } = useDemoData(ticker);

  // Every (strike-selection × exit-rule) combination is precomputed server-side;
  // changing a control swaps between static results client-side — no live inference.
  const comboKey = strikeMode === "delta" ? `delta${delta}_${exitRule}` : `otm${otm}_${exitRule}`;
  const backtest = data?.backtests?.[comboKey] || data?.backtest;
  const stats = backtest?.stats;
  const fmtUsd = v => `${v < 0 ? "−" : ""}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const btnStyle = on => ({
    fontFamily: "'JetBrains Mono', monospace",
    background: on ? "rgba(0,200,150,0.12)" : "transparent",
    color: on ? C.accent : C.muted,
    border: `1px solid ${on ? C.accent : C.border}`,
  });

  return (
    <SectionCard C={C} title="Backtest Lab" testid="demo-backtest-section"
      right={<TickerToggle value={ticker} onChange={setTicker} C={C} idPrefix="demo-bt-ticker" />}
      footer="Historical sample data (Jan 2024 – Dec 2025), illustrative only — not a recommendation to buy, sell, or hold any security. Identical output for every visitor.">
      <p className="mb-3 text-[11px] leading-relaxed max-w-3xl" style={{ color: C.muted }} data-testid="demo-backtest-explainer">
        This shows how a simple cash-secured-put strategy would have performed historically on the
        selected ticker, using your chosen strike selection (% OTM or target delta) and exit rule. Every
        visitor sees the same frozen 2024–2025 results — this is a historical simulation, not a live recommendation.
      </p>
      {error && <div className="h-32 flex items-center justify-center text-xs" style={{ color: C.muted }} data-testid="demo-bt-error">{error}</div>}
      {!error && !data && <div className="h-32 flex items-center justify-center text-xs" style={{ color: C.muted }} data-testid="demo-bt-loading">Loading demo data…</div>}
      {backtest && stats && (
        <>
          <div className="mb-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: C.muted, fontFamily: "'JetBrains Mono', monospace" }}
              data-testid="demo-backtest-label">
              {backtest.label}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-3" data-testid="demo-backtest-controls">
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] uppercase tracking-wider" style={{ color: "#555", fontFamily: "'JetBrains Mono', monospace" }}>Strike by</span>
              <button onClick={() => setStrikeMode("otm")} data-testid="demo-mode-otm"
                className="px-2.5 h-6 text-[10px] font-bold rounded transition-colors" style={btnStyle(strikeMode === "otm")}>
                % OTM
              </button>
              <button onClick={() => setStrikeMode("delta")} data-testid="demo-mode-delta"
                className="px-2.5 h-6 text-[10px] font-bold rounded transition-colors" style={btnStyle(strikeMode === "delta")}>
                Delta
              </button>
            </div>
            {strikeMode === "otm" ? (
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] uppercase tracking-wider" style={{ color: "#555", fontFamily: "'JetBrains Mono', monospace" }}>% OTM</span>
                {OTM_OPTIONS.map(o => (
                  <button key={o} onClick={() => setOtm(o)} data-testid={`demo-otm-${o}`}
                    className="px-2.5 h-6 text-[10px] font-bold rounded transition-colors" style={btnStyle(otm === o)}>
                    {o}%
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] uppercase tracking-wider" style={{ color: "#555", fontFamily: "'JetBrains Mono', monospace" }}>Target Δ</span>
                {DELTA_OPTIONS.map(dv => (
                  <button key={dv} onClick={() => setDelta(dv)} data-testid={`demo-delta-${dv}`}
                    className="px-2.5 h-6 text-[10px] font-bold rounded transition-colors" style={btnStyle(delta === dv)}>
                    .{dv}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[9px] uppercase tracking-wider" style={{ color: "#555", fontFamily: "'JetBrains Mono', monospace" }}>Exit</span>
              {EXIT_OPTIONS.map(([k, l]) => (
                <button key={k} onClick={() => setExitRule(k)} data-testid={`demo-exit-${k}`}
                  className="px-2.5 h-6 text-[10px] font-bold rounded transition-colors" style={btnStyle(exitRule === k)}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <EquityCurve backtest={backtest} C={C} />
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1 text-[10px]" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="demo-stats">
            <span style={{ color: C.text }}>Win rate <strong style={{ color: C.accent }}>{stats.win_rate}%</strong></span>
            <span style={{ color: C.text }}>Trades <strong>{stats.total_trades}</strong></span>
            <span style={{ color: C.text }}>Net P&amp;L <strong style={{ color: stats.total_pnl >= 0 ? C.accent : "#ff4d4d" }}>{fmtUsd(stats.total_pnl)}</strong></span>
            <span style={{ color: C.text }}>Max DD <strong style={{ color: "#ff4d4d" }}>{fmtUsd(-stats.max_drawdown)}</strong></span>
            <span style={{ color: C.text }}>Profit factor <strong>{stats.profit_factor >= 999 ? "∞" : stats.profit_factor}</strong></span>
          </div>
          <p className="mt-1.5 text-[10px] leading-relaxed" style={{ color: C.muted }}>
            Backtest of a mechanical, rules-based strategy over the fixed window using model-priced premium estimates — a historical simulation, not a forecast.
          </p>
        </>
      )}
    </SectionCard>
  );
};

export const PublicDemoWidget = ({ C }) => (
  <>
    <div id="regime-detection" style={{ scrollMarginTop: "60px" }}><RegimeSection C={C} /></div>
    <div id="backtest-lab" style={{ scrollMarginTop: "60px" }}><BacktestSection C={C} /></div>
  </>
);
